import type { ReactNode, Ref } from 'react';
import type { MyIcon } from '@repo/icons';
import type { ParkingPropertyFilter, ParkingPropertyFilters } from '@repo/domain';
import type { TestId } from '@repo/test-id';
import { tr } from '@repo/locale';
import { Popover } from '@repo/ui';

export type FilterSubPropertyEntry = [ParkingPropertyFilter, { nameTranslationKey: string; icon: MyIcon }];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  renderTrigger: (triggerRef: Ref<HTMLButtonElement>) => ReactNode;
  testId: TestId;
  subProperties: FilterSubPropertyEntry[];
  filters: ParkingPropertyFilters;
  onToggleSubProperty: (subPropertyKey: ParkingPropertyFilter) => void;
}

export default function FilterSubPropertiesPopover({
  isOpen,
  onClose,
  renderTrigger,
  testId,
  subProperties,
  filters,
  onToggleSubProperty,
}: Props) {
  return (
    <Popover<HTMLButtonElement>
      isOpen={isOpen}
      onClose={onClose}
      renderTrigger={renderTrigger}
      zIndex="z-above-modal"
      className="flex flex-col"
      testId={testId.append('powerOptions')}
    >
      {subProperties.map(([subPropertyKey, definition]) => {
        const subPropertyLabel = tr(definition.nameTranslationKey);
        const SubPropertyIcon = definition.icon;
        const handleSubPropertyClick = () => onToggleSubProperty(subPropertyKey);

        return (
          <button
            key={subPropertyKey}
            className="text-body hover:bg-stroke-base-soft/50 flex cursor-pointer items-center gap-2 px-4 py-2 text-sm"
            data-testid={testId.append('option').append(subPropertyKey)}
            onClick={handleSubPropertyClick}
          >
            <SubPropertyIcon size={32} className={filters[subPropertyKey] ? 'text-brand' : 'text-body'} />
            <div className="pt-1">{subPropertyLabel}</div>
          </button>
        );
      })}
    </Popover>
  );
}
